import React, { createContext, useContext, useState, useEffect } from 'react';
import { CapacitorHttp } from '@capacitor/core';
import i18n from '../i18n';
import { getSession, clearSession, isNativePlatform } from '../utils/nativeAuth';

const AuthContext = createContext();

// Get API URL - use production for native apps
const getApiUrl = () => {
  if (isNativePlatform()) {
    return 'https://api.shkalot.xyz';
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:5001';
};

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is authenticated
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    try {
      const API_URL = getApiUrl();
      
      if (isNativePlatform()) {
        // Use CapacitorHttp for native platforms
        const token = await getSession();
        if (!token) {
          setUser(null);
          setLoading(false);
          return;
        }
        
        const response = await CapacitorHttp.get({
          url: `${API_URL}/api/auth/verify`,
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.status === 200) {
          const data = response.data;
          setUser(data.user);
          if (data.user?.ui_language && data.user.ui_language !== i18n.language) {
            i18n.changeLanguage(data.user.ui_language);
          }
        } else {
          setUser(null);
          await clearSession();
        }
      } else {
        // Use fetch for web
        const response = await fetch(`${API_URL}/api/auth/verify`, {
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
          if (data.user?.ui_language && data.user.ui_language !== i18n.language) {
            i18n.changeLanguage(data.user.ui_language);
          }
        } else {
          setUser(null);
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Helper to check if we're on the app subdomain
  const isAppSubdomain = () => {
    if (typeof window === 'undefined') return false;
    return window.location.hostname === 'app.shkalot.xyz';
  };

  const logout = async () => {
    try {
      const API_URL = getApiUrl();
      
      if (isNativePlatform()) {
        const token = await getSession();
        if (token) {
          await CapacitorHttp.post({
            url: `${API_URL}/api/auth/logout`,
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
        }
        await clearSession();
      } else {
        await fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          credentials: 'include',
        });
      }
      
      setUser(null);
      
      // Redirect to main domain after logout (web only, on app subdomain)
      if (!isNativePlatform() && isAppSubdomain()) {
        window.location.href = 'https://shkalot.xyz';
      }
    } catch (error) {
      console.error('Logout failed:', error);
      if (isNativePlatform()) {
        await clearSession();
      }
      setUser(null);
    }
  };

  const value = {
    user,
    loading,
    logout,
    refreshAuth: checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
